import { PromptLibraryItem } from "@/modules/promptLibrary/promptLibrary.types";
import { MarkdownRenderer } from "@/components/feature/markdown/markdownRenderer";
import { DirectoryUser } from "@/types/user";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { Star } from "lucide-react";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { Button } from "@/components/ui/button";

interface Props {
  item: PromptLibraryItem;
  user: DirectoryUser;
  onDelete: (item: PromptLibraryItem) => void;
  onFavorite: (item: PromptLibraryItem) => void;
  onEdit: (item: PromptLibraryItem) => void;
  onSendToChat: (item: PromptLibraryItem) => void;
}

export const PromptLibraryTableItem: React.FC<Props> = ({ item, user, onDelete, onFavorite, onEdit, onSendToChat }) => {
  const { formatDate } = useDateFormatter();
  const currentUser = useAuthenticatedUser();

  return (
    <div className="w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      {/* Header Section */}
      <div className="border-b border-gray-100 bg-[#F3F1EB] px-6 py-4">
        <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-gray-900">
          {item.name}
          <Star className="mr-2 h-5 w-5" fill={item.is_favorite ? "current-color" : "none"} />
        </h2>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>
            {user?.name} {user?.surname}
          </span>
          <span>•</span>
          <span>{formatDate(item.created_at)}</span>
          <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600">
            {item.visibility === "public" ? "Public" : "Private"}
          </span>
        </div>
      </div>

      {/* Body Section */}
      <div className="p-6">
        <p className="mb-4 text-sm leading-relaxed text-gray-800">
          <MarkdownRenderer content={item.prompt} evidences={[]} processCitations={false} />
        </p>

        {/* Notes Section */}
        <div className="mb-8 rounded-r-md border-l-4 border-red-300 bg-[#FAF9F6] p-4">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-bold text-gray-900">Notes:</span>
            <p className="text-sm text-gray-600">{item.notes}</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            onClick={() => onDelete(item)}
            className="rounded-lg border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-offset-2"
            disabled={currentUser.id !== item.user_id}
          >
            Delete
          </Button>
          <Button
            onClick={() => onFavorite(item)}
            className="rounded-lg border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-offset-2"
          >
            Favourite
          </Button>
          <Button
            onClick={() => onEdit(item)}
            className="rounded-lg border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-offset-2"
            disabled={currentUser.id !== item.user_id}
          >
            Edit
          </Button>
          <Button
            onClick={() => onSendToChat(item)}
            disabled={item.source === "open_chat" && !currentUser.organization.features.includes("open_chat")}
            className="rounded-lg border border-transparent bg-[#1A1A1A] px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2"
          >
            Send to Chat
          </Button>
        </div>
      </div>
    </div>
  );
};
